import { Action } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, race, take } from 'redux-saga/effects';
import {
    DefaultDevBoxTunnelHostingResourceName,
    DefaultDevBoxTunnelName,
    DevBoxTunnelErrorCode,
} from '../../../constants/add-ons';
import { PerformanceMetric } from '../../../constants/telemetry';
import {
    DevBoxTunnelTypePrivatePreview,
    ListDevBoxAddOnsResponse,
    listDevBoxAddOns,
} from '../../../data/services/data-plane-api/add-ons';
import { GetDevBoxResponse, getDevBox } from '../../../data/services/data-plane-api/dev-box';
import { ClientError, FailureOperation, isFailureResponse } from '../../../models/common';
import { findPrimaryTunnelAddon } from '../../../utilities/add-ons';
import { getDevBoxState } from '../../../utilities/dev-box';
import { createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault } from '../../../utilities/failure';
import { isUndefinedOrWhiteSpace } from '../../../utilities/string';
import { trackTimedPerformanceMetric } from '../../../utilities/telemetry/channel';
import { createOptionsForDataPlaneResourceMetric } from '../../../utilities/telemetry/helpers';
import {
    createOrReplaceDevBoxAddOn,
    enableDevBoxAddOn,
    enableDevBoxTunnel,
    enableDevBoxTunnelError,
    enableDevBoxTunnelFailed,
    enableDevBoxTunnelPhaseStartDevBox,
    enableDevBoxTunnelPhaseTunnelEnabling,
    enableDevBoxTunnelPhaseTunnelEnablingFinished,
    enableDevBoxTunnelPhaseTunnelNotEnabled,
    enableDevBoxTunnelSuccess,
    pollNonTerminalDevBoxAddOn,
} from '../../actions/add-ons/add-ons-action-creators';
import { EnableTunnelAction } from '../../actions/add-ons/add-ons-actions';
import {
    startDevBox as startDevBoxAction,
    startDevBoxError,
    startDevBoxFailed,
    startDevBoxSuccess,
} from '../../actions/dev-box/dev-box-action-creators';
import { DevBoxActionType } from '../../actions/dev-box/dev-box-actions';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForDevCenterDataPlanePayload } from '../../actions/identity/identity-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeEvery } from '../../effects/take';
import { AsyncOutcome } from '../../store/common-state';
import { DevBoxState } from '../../store/dev-box-state';

export function* enableDevBoxTunnelSaga(action: EnableTunnelAction): SagaIterator {
    const startTime = new Date();
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id, tunnelName } = payload;

    try {
        const accessToken: string = yield putAndAwait(
            getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
        );

        // Step 1: Check current DevBox state
        const getDevBoxResponse: GetDevBoxResponse = yield call(getDevBox, id, accessToken, activityId);
        if (isFailureResponse(getDevBoxResponse)) {
            yield put(enableDevBoxTunnelFailed({ failure: getDevBoxResponse, id }, meta));
            return;
        }
        const { data: devBox } = getDevBoxResponse;
        const currentDevBoxState = getDevBoxState(devBox);

        // Step 2: Start the DevBox only if it's not already running
        if (currentDevBoxState !== DevBoxState.Running) {
            // Dispatch phase: Starting DevBox
            yield put(enableDevBoxTunnelPhaseStartDevBox({ id }, meta));

            // Dispatch the start action (this sets the DevBox state to "Starting")
            yield put(startDevBoxAction({ id }, meta));

            // We use race here instead of polling to wait for the start action to complete, to avoid two simultaneous requests for get DevBox
            const { success, error, failed } = yield race({
                success: take((action: Action) => {
                    if (action.type === DevBoxActionType.StartDevBoxSuccess) {
                        const specificAction = action as ReturnType<typeof startDevBoxSuccess>;
                        return specificAction.payload?.id === id;
                    }
                    return false;
                }),
                error: take((action: Action) => {
                    if (action.type === DevBoxActionType.StartDevBoxError) {
                        const specificAction = action as ReturnType<typeof startDevBoxError>;
                        return specificAction.payload?.id === id;
                    }
                    return false;
                }),
                failed: take((action: Action) => {
                    if (action.type === DevBoxActionType.StartDevBoxFailed) {
                        const specificAction = action as ReturnType<typeof startDevBoxFailed>;
                        return specificAction.payload?.id === id;
                    }
                    return false;
                }),
            });

            if (error || failed) {
                const failureAction = error || failed;
                yield put(enableDevBoxTunnelFailed({ failure: failureAction.payload.failure, id }, meta));
                return;
            }

            // Verify the DevBox is running
            const { result: startedDevBox } = success.payload;

            const startedDevBoxState = getDevBoxState(startedDevBox);
            if (startedDevBoxState !== DevBoxState.Running) {
                const failure = createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
                    startedDevBox.error,
                    FailureOperation.StartDevBox
                );
                yield put(enableDevBoxTunnelFailed({ failure, id }, meta));
                return;
            }
        }

        // Step 3: List current addons to find tunnel addon
        const listAddOnsResponse: ListDevBoxAddOnsResponse = yield call(listDevBoxAddOns, id, accessToken, activityId);
        if (isFailureResponse(listAddOnsResponse)) {
            yield put(enableDevBoxTunnelFailed({ failure: listAddOnsResponse, id }, meta));
            return;
        }
        const { data: devBoxAddOns } = listAddOnsResponse;

        const tunnelAddOn = findPrimaryTunnelAddon(devBoxAddOns);

        // Step 4: Enable tunnel addon if found, create it if it doesn't exist
        // We'll always enable the tunnel, even if it can already be connected, to avoid service-side inconsistencies
        const devBoxTunnelName = isUndefinedOrWhiteSpace(tunnelName) ? DefaultDevBoxTunnelName : tunnelName;

        yield put(enableDevBoxTunnelPhaseTunnelEnabling({ id }, meta));
        if (!tunnelAddOn) {
            // Create tunnel addon if it doesn't exist
            const createResponse = yield putAndAwait(
                createOrReplaceDevBoxAddOn(
                    {
                        id,
                        name: devBoxTunnelName,
                        // Using the 0301 private preview API constant
                        // Task 2443329: Use DevBoxAddOnType.DevBoxTunnel when 0401 API is ready
                        kind: DevBoxTunnelTypePrivatePreview,
                        hostingResourceName: DefaultDevBoxTunnelHostingResourceName,
                    },
                    meta
                )
            );

            if (isFailureResponse(createResponse)) {
                // Check for specific error codes
                if (createResponse.code === DevBoxTunnelErrorCode.DevBoxTunnelNotEnabled) {
                    // Handle the specific DevBoxTunnelNotEnabled error, which means the tunnel is not enabled in pool.
                    yield put(enableDevBoxTunnelPhaseTunnelNotEnabled({ id }, meta));
                    return;
                }

                yield put(enableDevBoxTunnelFailed({ failure: createResponse, id }, meta));
                return;
            }
        }

        // Enable the tunnel addon
        const enableResponse = yield putAndAwait(enableDevBoxAddOn({ id, name: devBoxTunnelName }, meta));
        if (isFailureResponse(enableResponse)) {
            // Check for specific error codes
            if (enableResponse.code === DevBoxTunnelErrorCode.DevBoxTunnelNotEnabled) {
                // Handle the specific DevBoxTunnelNotEnabled error, which means the tunnel is not enabled in pool.
                yield put(enableDevBoxTunnelPhaseTunnelNotEnabled({ id }, meta));
                return;
            }

            yield put(enableDevBoxTunnelFailed({ failure: enableResponse, id }, meta));
            return;
        }

        // Then poll until addon is enabled
        const pollResponse = yield putAndAwait(
            pollNonTerminalDevBoxAddOn({ id, name: devBoxTunnelName, initialDelayMs: 5000, intervalMs: 10000 })
        );
        if (isFailureResponse(pollResponse)) {
            yield put(enableDevBoxTunnelFailed({ failure: pollResponse, id }, meta));
            return;
        }

        // Return success - addon polling completed successfully or no polling needed
        yield put(enableDevBoxTunnelPhaseTunnelEnablingFinished({ id }, meta));
        yield put(enableDevBoxTunnelSuccess({ id }, meta));
        yield resolveAction(action, { id });

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.StartDevBox,
            startTime,
            AsyncOutcome.Success,
            createOptionsForDataPlaneResourceMetric(id, activityId)
        );
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.StartDevBox);
        yield put(enableDevBoxTunnelPhaseTunnelEnablingFinished({ id }, meta));
        yield put(enableDevBoxTunnelError({ error, id }, meta));
        yield rejectAction(action, error);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.StartDevBox,
            startTime,
            AsyncOutcome.Error,
            createOptionsForDataPlaneResourceMetric(id, activityId, error.code)
        );
    }
}

export function* enableDevBoxTunnelListenerSaga(): SagaIterator {
    yield takeEvery(enableDevBoxTunnel, enableDevBoxTunnelSaga);
}
